import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";

//Components
import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { Button } from "Components";
import { Tooltip } from "Components";
import TableView from "Components/TableView";
import { Chip, IconButton, Card, CardContent, Typography, Box } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import DataDetailsModal from "./components/DataDetailsModal";

//Actions
import { fetchTransactionDetails, closeModalImportResults, clearErrorModalImport } from './redux/slice';

//Styles
import './importResults.scss';

const ImportResultsModal = () => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableViewRef = useRef();
    
    // Local state for data details modal
    const [dataDetailsModal, setDataDetailsModal] = useState({
        open: false,
        data: null,
        importType: null
    });

    //Redux States
    const { 
        isOpen, 
        loading, 
        results, 
        error, 
        transactionData,
        nextToken
    } = useSelector(state => state.importResultsModal);

    const handleClose = () => {
        dispatch(closeModalImportResults());
    };

    const handleRefresh = () => {
        if (transactionData?.transactionId) {
            // Clear existing results and fetch fresh data
        dispatch(fetchTransactionDetails(transactionData.transactionId));

        }
    };

    const handleLoadMore = () => {
        if (transactionData?.transactionId && nextToken) {
            dispatch(fetchTransactionDetails({
                transactionId: transactionData.transactionId,
                nextToken: nextToken
            }));
        }
    };

    const handleShowDataDetails = (parsedData, importType) => {
        setDataDetailsModal({
            open: true,
            data: parsedData,
            importType
        });
    };

    const handleCloseDataDetails = () => {
        setDataDetailsModal({
            open: false,
            data: null,
            importType: null
        });
    };

    const parseDataField = (dataString) => {
        try {
            return JSON.parse(dataString);
        } catch {
            return null;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'processed':
                return { color: '#4caf50', label: messages['import_status_processed'] };
            case 'failure':
                return { color: '#f44336', label: messages['import_status_failure'] };
            case 'pending':
                return { color: '#ff9800', label: messages['import_status_pending'] };
            default:
                return { color: '#757575', label: status || messages['import_status_unknown'] };
        }
    };

    const formatImportType = (type) => {
        return type?.replace('Import', '') || 'Unknown';
    };

    const getIdentifier = (parsedData, importType) => {
        if (importType === 'ImportUnits' && parsedData?.unit?.esn) {
            return { label: 'ESN', value: parsedData.unit.esn };
        }
        else if (importType === 'ImportTags') {
            if (parsedData?.tag?.id) {
                return { label: 'ID', value: parsedData.tag.id };
            }
            if (parsedData?.tag?.label) {
                return { label: 'Tag Label', value: parsedData.tag.label };
            }
        }
        else if (importType === 'ImportDrivers') {
            if (parsedData?.driver?.id) {
                return { label: 'ID', value: parsedData.driver.id };
            }
            if (parsedData?.driver?.name) {
                return { label: 'Driver Name', value: parsedData.driver.name };
            }
            if (parsedData?.driver?.externalId) {
                return { label: 'Code', value: parsedData.driver.externalId };
            }
        }
        else if (importType === 'ImportUsers') {
            if (parsedData?.user?.id) {
                return { label: 'ID', value: parsedData.user.id };
            }
            if (parsedData?.user?.firstName && parsedData?.user?.lastName) {
                return { label: 'Name', value: parsedData.user.firstName + ' ' + parsedData.user.lastName };
            }
        }
        else if (importType === 'ImportAssets') {
            if (parsedData?.asset?.id) {
                return { label: 'ID', value: parsedData.asset.id };
            }
            if (parsedData?.asset?.label) {
                return { label: 'Label', value: parsedData.asset.label };
            }
        }
        
        return null;
    };

    const renderTransactionRow = (item, index) => {
        const parsedData = parseDataField(item.Data);
        const status = getStatusColor(item.Status);
        const identifier = getIdentifier(parsedData, item.Type);
        const showMessage = item.Status === 'Failure' && item.Message;
        const errorCode = item.Message?.split('_')[0] || item.Message;

        return (
            <Card 
                key={`${item.TransactionId}-${index}`}
                className="transaction-card"
                sx={{ margin: 1, border: '1px solid #e0e0e0' }}
            >
                <CardContent sx={{ padding: 2 }}>
                    {/* Header with Status and Info Icon */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="textSecondary" fontWeight={700}>
                                {messages['import_status']}:
                            </Typography>
                            <Chip
                                label={status.label}
                                size="small"
                                style={{
                                    backgroundColor: status.color,
                                    color: "#ffffff"
                                }}
                            />
                        </Box>
                        {parsedData && (
                            <Tooltip title={messages['import_show_data'] || 'Show Data'}>
                                <IconButton 
                                    size="small"
                                    onClick={() => handleShowDataDetails(parsedData, item.Type)}
                                >
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {/* Identifier */}
                    {identifier && (
                        <Box display="flex" alignItems="center" marginBottom={showMessage ? 1 : 0}>
                            <Typography variant="caption" color="textSecondary" fontWeight={700} marginRight={1}>
                                {identifier.label}:
                            </Typography>
                            <Typography 
                                variant="body2"
                                sx={{
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'visible',
                                    maxWidth: '100%'
                                }}
                            >
                                {identifier.value}
                            </Typography>
                        </Box>
                    )}

                    {/* Message - only for failures */}
                    {showMessage && (
                        <Box>
                            <Typography variant="caption" color="textSecondary" fontWeight={700}>
                                {messages['import_data_message']}:
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="error"
                                fontWeight={500}
                                sx={{ 
                                    marginTop: 0.5,
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap',
                                    overflow: 'visible',
                                    maxWidth: '100%'
                                }}
                            >
                                {(messages[`import_errormsg_${errorCode}`] || item.Message)
                                    .replace('{{unitLabel}}', parsedData?.unit?.label || '')
                                    .replace('{{unitId}}', parsedData?.unit?.id || '')
                                    .replace('{{unitDriver}}', parsedData?.unit?.driver || '')
                                    .replace('{{defaultDriver}}', parsedData?.unit?.defaultDriver || '')
                                    .replace('{{unitEsn}}', parsedData?.unit?.esn || '')
                                    .replace('{{engineHours}}', parsedData?.unit?.engineHours || '')
                                    .replace('{{odometer}}', parsedData?.unit?.odometer || '')
                                    .replace('{{unitVin}}', parsedData?.unit?.vehicleInformation?.vin || '')
                                    .replace('{{unitMake}}', parsedData?.unit?.vehicleInformation?.make || '')
                                    .replace('{{vehicleYear}}', parsedData?.unit?.vehicleInformation?.year || '')
                                    .replace('{{unitModel}}', parsedData?.unit?.vehicleInformation?.model || '')
                                    .replace('{{unitAssetType}}', parsedData?.unit?.zonarAsset?.assetType || '')
                                    .replace('{{unitAssetSubtype}}', parsedData?.unit?.zonarAsset?.assetSubtype || '')
                                    .replace('{{unitRegisCountry}}', parsedData?.unit?.zonarAsset?.registrationCountry || '')
                                    .replace('{{unitRegisState}}', parsedData?.unit?.zonarAsset?.registrationState || '')
                                    .replace('{{unitRegisExpDate}}', parsedData?.unit?.zonarAsset?.registrationExpirationDate || '')
                                    .replace('{{unitLicensePlate}}', parsedData?.unit?.zonarAsset?.licensePlate || '')
                                    .replace('{{unitMaintenanceCompany}}', parsedData?.unit?.zonarAsset?.maintenanceCompany || '')
                                    .replace('{{unitEngineSerialNumber}}', parsedData?.unit?.zonarAsset?.engineSerialNumber || '')
                                    .replace('{{unitChassisSerialNumber}}', parsedData?.unit?.zonarAsset?.chassisSerialNumber || '')
                                    .replace('{{unitInspectionDeviceType}}', parsedData?.unit?.zonarAsset?.inspectionDeviceType || '')
                                    .replace('{{unitInspectionTagId}}', parsedData?.unit?.zonarAsset?.inspectionTagId || '')
                                    .replace('{{tagName}}', parsedData?.tags?.[0] || '')
                                    .replace('{{tagLabel}}', parsedData?.tag?.label || '')
                                    .replace('{{tagId}}', parsedData?.tag?.id || '')
                                    .replace('{{parentLabel}}', parsedData?.tag?.parentLabel || '')
                                    .replace('{{driverName}}', parsedData?.driver?.name || '')
                                    .replace('{{driverId}}', parsedData?.driver?.id || '')
                                    .replace('{{driverExternalId}}', parsedData?.driver?.externalId || '')
                                    .replace('{{driverEmail}}', parsedData?.driver?.email || '')
                                    .replace('{{driverPin}}', parsedData?.driver?.pinNumber || '')
                                    .replace('{{driverLicense}}', parsedData?.driver?.licenseNumber || '')
                                    .replace('{{driverCode}}', parsedData?.driver?.code || '')
                                    .replace('{{assetId}}', parsedData?.asset?.id || '')
                                    .replace('{{assetLabel}}', parsedData?.asset?.label || '')
                                    .replace('{{assetTypeName}}', parsedData?.asset?.assetTypeName || '')
                                    .replace('{{assetSubtypeName}}', parsedData?.asset?.assetSubtypeName || '')
                                    .replace('{{assetVin}}', parsedData?.asset?.vin || '')
                                    .replace('{{assetMake}}', parsedData?.asset?.make || '')
                                    .replace('{{assetModel}}', parsedData?.asset?.model || '')
                                    .replace('{{assetYear}}', parsedData?.asset?.year || '')
                                    .replace('{{assetStatus}}', parsedData?.asset?.status || '')
                                    .replace('{{assetExternalId}}', parsedData?.asset?.externalId || '')
                                    .replace('{{tagNameAsset}}', parsedData?.tagNames?.[0] || '')
                                    .replace('{{assetMaintenanceCompany}}', parsedData?.asset?.maintenanceCompany || '')
                                    .replace('{{assetRegisCountry}}', parsedData?.asset?.registrationCountry || '')
                                    .replace('{{assetRegisState}}', parsedData?.asset?.registrationState || '')
                                    .replace('{{assetRegisExpiration}}', parsedData?.asset?.registrationExpiration || '')
                                    .replace('{{assetLicensePlate}}', parsedData?.asset?.licensePlate || '')
                                    .replace('{{assetEngSerialNumber}}', parsedData?.asset?.engineSerialNumber || '')
                                    .replace('{{assetChasSerialNumber}}', parsedData?.asset?.chassisSerialNumber || '')
                                    .replace('{{assetPowerUnitNumber}}', parsedData?.asset?.powerUnitNumber || '')
                                    .replace('{{assetInspectionDeviceType}}', parsedData?.asset?.inspectionDeviceType || '')
                                    .replace('{{userName}}', (parsedData?.user?.['First Name'] + ' ' + parsedData?.user?.['Last Name']) || '')
                                    .replace('{{userId}}', parsedData?.user?.id || '')
                                    .replace('{{userEmail}}', parsedData?.user?.email || '')
                                    .replace('{{userTag}}', parsedData?.user?.tags || '')
                                    .replace('{{userRole}}', parsedData?.user?.role || '')
                                    .replace('{{employeeNumber}}', parsedData?.driver?.employeeNumber || '')
                                    .replace('{{keyFob}}', parsedData?.driver?.keyFob || '')
                                    .replace('{{licenseNumber}}', parsedData?.driver?.licenseNumber || '')
                                    .replace('{{driverPhone}}', parsedData?.driver?.phone || '')
                                    .replace('{{inspectionTagId}}', parsedData?.asset?.inspectionTagId || '')
                                    .replace('{{userExpiryDate}}', parsedData?.user?.expiryDate || '')
                                    .replace('{{driverRegisCountry}}', parsedData?.driver?.registrationCountry || '')
                                    .replace('{{driverRegisState}}', parsedData?.driver?.registrationState || '')
                                    .replace('{{driverHosRuleset}}', parsedData?.driver?.hosRuleset || '')
                                    .replace('{{driverTimezone}}', parsedData?.driver?.timezone || '')
                                    .replace('{{driverVehicle}}', parsedData?.driver?.vehicle || '')
                                    .replace('{{driverSupervisor}}', parsedData?.driver?.supervisor || '')

                                }
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    useEffect(() => {
        if (isOpen && transactionData?.transactionId) {
            dispatch(fetchTransactionDetails(transactionData.transactionId));
        }
    }, [isOpen, transactionData?.transactionId, dispatch]);

    if (!isOpen || !transactionData) {
        return null;
    }

    return (
        <>
            <Modal
                open={isOpen}
                title={`${messages['import_results_title'] || 'Transaction Details'} - ${formatImportType(transactionData.importType)}`}
                loading={false}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    {/* Error Display */}
                    {error && (
                        <Box 
                            sx={{ 
                                padding: 2, 
                                marginBottom: 2, 
                                backgroundColor: '#ffebee', 
                                color: '#c62828',
                                borderRadius: 1,
                                border: '1px solid #ffcdd2',
                                position: 'relative'
                            }}
                        >
                            {error}
                            <IconButton
                                size="small"
                                onClick={() => dispatch(clearErrorModalImport())}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                            >
                                ×
                            </IconButton>
                        </Box>
                    )}

                    <div className="container-transaction-table">
                        <div>
                            <Tooltip title={messages['refresh'] || 'Refresh'}>
                                <IconButton 
                                    style={{ padding: 0, marginLeft: 5 }} 
                                    onClick={handleRefresh} 
                                    size="large"
                                    disabled={loading}
                                >
                                    <RefreshIcon style={{ color: 'black', fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                        
                        <TableView
                            ref={tableViewRef}
                            data={results || []}
                            loading={loading}
                            page={1}
                            rowsPerPage={25}
                            topSpaceSize={227}
                            infiniteScroll={true}
                            viewType="list"
                            messages={messages}
                            serverSide={true}
                            itemSelector={false}
                            onChangePage={() => {
                                handleLoadMore();
                            }}
                            moreRowsAvailable={!!nextToken}
                            renderRow={renderTransactionRow}
                            noDataMessage={messages['import_no_details_available'] || 'No transaction details available'}
                        />
                    </div>
                </ModalBody>

                <ModalFooter>
                    <Button
                        className="btn-dark-blue"
                        onClick={handleClose}
                    >
                        {messages['close'] || 'Close'}
                    </Button>
                </ModalFooter>
            </Modal>

            <DataDetailsModal 
                open={dataDetailsModal.open}
                onClose={handleCloseDataDetails}
                data={dataDetailsModal.data}
                importType={dataDetailsModal.importType}
                messages={messages}
            />
        </>
    );
};

export default ImportResultsModal;